import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useApi } from '../utils/useApi';
import { setDateFormat } from '../utils/format';
import type { ShopDetails } from '../types';

interface ShopContextValue {
  shop: ShopDetails | null;
  loading: boolean;
  error: string | null;
  /** Convenience: currency for money formatting (falls back to USD). */
  currency: string;
  refetch: () => Promise<void>;
}

const ShopContext = createContext<ShopContextValue>({
  shop: null,
  loading: true,
  error: null,
  currency: 'USD',
  refetch: async () => {},
});


/**
 * Loads shop information once from `/api/shop-details` (authenticated with the
 * App Bridge session token) after app initialization and caches it in React
 * Context for the whole app. Replaces any reliance on encrypted shop sessions.
 */
export function ShopProvider({ children }: { children: ReactNode }) {
  const { data, loading, error, refetch } = useApi<ShopDetails>('/shop-details', undefined, []);
  // Hydrate the app-wide date format from the merchant's settings (once).
  const { data: settings } = useApi<{ extra?: { general?: { dateFormat?: string } } }>('/settings', undefined, []);
  useEffect(() => {
    setDateFormat(settings?.extra?.general?.dateFormat);
  }, [settings]);
  const value: ShopContextValue = {
    shop: data,
    loading,
    error,
    currency: data?.currency || 'USD',
    refetch,
  };
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

/** Access the cached shop details from anywhere in the app. */
export function useShop() {
  return useContext(ShopContext);
}
